"use client";
import React, { useState, useEffect } from "react";
import { Typography, Container, Box, Paper } from "@mui/material";
import { Plus_Jakarta_Sans, Inter } from "next/font/google";
import Link from "next/link";
import { useTranslation } from "react-i18next";

const PlusJakartaSans = Plus_Jakarta_Sans({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
});

const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

const TableOfContents = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { threshold: 0.5 },
    );

    document.querySelectorAll("h1, h2, h3, h4").forEach((heading) => {
      observer.observe(heading);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const tocItems = [
    { id: "overview", titleKey: "getgeekArticle.toc.overview", indent: 0 },
    { id: "top-7-ways", titleKey: "getgeekArticle.toc.top7Ways", indent: 0 },
    {
      id: "employee-referral",
      titleKey: "getgeekArticle.toc.employeeReferral",
      indent: 1,
    },
    {
      id: "quality-over-quantity",
      titleKey: "getgeekArticle.toc.qualityOverQuantity",
      indent: 1,
    },
    {
      id: "automate-recruitment",
      titleKey: "getgeekArticle.toc.automateRecruitment",
      indent: 1,
    },
    {
      id: "remote-interview",
      titleKey: "getgeekArticle.toc.remoteInterview",
      indent: 1,
    },
    {
      id: "social-media",
      titleKey: "getgeekArticle.toc.socialMedia",
      indent: 1,
    },
    { id: "talent-pool", titleKey: "getgeekArticle.toc.talentPool", indent: 1 },
    {
      id: "talent-acquisitions",
      titleKey: "getgeekArticle.toc.talentAcquisitions",
      indent: 1,
    },
    {
      id: "ending-notes",
      titleKey: "getgeekArticle.toc.endingNotes",
      indent: 0,
    },
  ];

  return (
    <Paper
      elevation={3}
      sx={{
        position: "fixed",
        display: { xs: "none", sm: "none", md: "none", xl: "none" },
        right: { xs: "5%", sm: "10%" },
        top: { xs: "50%", sm: "66.6%" },
        transform: "translateY(-50%)",
        width: "280px",
        p: 2,
        maxHeight: "calc(80vh - 20%)",
        overflowY: "auto",
        scrollBehavior: "smooth",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontFamily: PlusJakartaSans.style.fontFamily, fontWeight: "700" }}
      >
        {t("getgeekArticle.toc.title")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {tocItems.map(({ id, titleKey, indent }) => (
          <Typography
            key={id}
            onClick={() => scrollToSection(id)}
            sx={{
              cursor: "pointer",
              pl: isArabic ? 0 : indent * 2,
              pr: isArabic ? indent * 2 : 0,
              color: activeId === id ? "primary.main" : "#4B5963",
              fontFamily: inter.style.fontFamily,
              fontSize: "14px",
              textAlign: isArabic ? "right" : "left",
              "&:hover": { color: "primary.main" },
            }}
          >
            {t(titleKey)}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

const GetgeekArticleContent = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const paragraphSx = {
    mb: ".75%",
    fontFamily: inter.style.fontFamily,
    fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
    color: "#4B5963",
    fontWeight: "400",
    textAlign: isArabic ? "right" : "left",
    direction: isArabic ? "rtl" : "ltr",
  };

  return (
    <Container maxWidth="xl">
      <TableOfContents />
      <Paper elevation={2} sx={{ my: 4, border: "none", boxShadow: "none" }}>
        <Typography sx={paragraphSx}>{t("getgeekArticle.p1")}</Typography>
        <Typography sx={paragraphSx}>{t("getgeekArticle.p2")}</Typography>
        <Typography sx={paragraphSx}>{t("getgeekArticle.p3")}</Typography>
        <Typography sx={paragraphSx}>{t("getgeekArticle.p4")}</Typography>
        <Typography sx={paragraphSx}>{t("getgeekArticle.p5")}</Typography>
        <Typography sx={paragraphSx}>{t("getgeekArticle.p6")}</Typography>
        <Typography sx={paragraphSx}>{t("getgeekArticle.p7")}</Typography>
        <Typography sx={paragraphSx}>{t("getgeekArticle.p8")}</Typography>

        <Typography sx={paragraphSx}>
          <span style={{ fontWeight: "bold" }}>
            {t("getgeekArticle.bulkTitle")}
          </span>{" "}
          {t("getgeekArticle.bulkDesc")}
        </Typography>

        <Typography sx={paragraphSx}>
          <span style={{ fontWeight: "bold" }}>
            {t("getgeekArticle.customTitle")}
          </span>{" "}
          {t("getgeekArticle.customDesc")}
        </Typography>

        <Typography sx={paragraphSx}>
          <span style={{ fontWeight: "bold" }}>
            {t("getgeekArticle.scheduleTitle")}
          </span>{" "}
          {t("getgeekArticle.scheduleDesc")}
        </Typography>

        <Typography sx={paragraphSx}>{t("getgeekArticle.p9")}</Typography>
        <Typography sx={paragraphSx}>{t("getgeekArticle.p10")}</Typography>

        <Typography sx={paragraphSx}>
          {t("getgeekArticle.signupText")}{" "}
          <Link
            href="https://luckyrecruiter.getgeek.ai/Register"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#9048FB",
              textDecoration: "none",
              marginLeft: isArabic ? "0" : ".3%",
              marginRight: isArabic ? ".3%" : "0",
            }}
          >
            https://luckyrecruiter.getgeek.ai/Register
          </Link>
        </Typography>
      </Paper>
    </Container>
  );
};

export default GetgeekArticleContent;
